<template>
  <FullScreenOverlayLoader
    :isLoading="isLoading"
    :message="'Processing image...'"
  />
  <Dialog
    v-model:visible="localVisible"
    modal
    maximizable
    @show="showImageEditDialog"
    ref="imageEditDialogRef"
    :style="{ width: '50rem' }"
    :breakpoints="{ '1199px': '100vw', '575px': '100vw' }"
    class="image-edit-dialog"
  >
    <!-- Custom header template that properly accommodates dialog buttons -->
    <template #header>
      <div
        class="truncate max-w-[calc(100%-4rem)]"
        v-tooltip.bottom="name"
      >
        {{ name }}
      </div>
    </template>

    <div class="relative">
      <div
        v-if="Helper.isImage({ type: type }) && !isLoading"
        class="absolute top-2 right-2 z-30 flex gap-2"
      >
        <Tag
          v-tooltip.top="`Rotate`"
          @click="rotateImage"
          size="large"
          severity="info"
          style="cursor: pointer"
        >
          <i
            class="pi pi-refresh text-lg"
            style="font-size: 1.25rem"
          />
        </Tag>
        <Tag
          v-tooltip.top="showCropper ? 'Remove Crop' : 'Toggle Crop'"
          @click="toggleCropper"
          size="large"
          :severity="showCropper ? 'error' : 'info'"
          style="cursor: pointer"
        >
          <i
            :class="
              showCropper ? 'pi pi-times text-lg' : 'pi pi-expand text-lg'
            "
            style="font-size: 1.25rem"
          />
        </Tag>
        <Tag
          v-if="showCropper"
          v-tooltip.top="`Apply Crop`"
          @click="applyCrop"
          size="large"
          severity="success"
          style="cursor: pointer"
        >
          <i
            class="pi pi-check text-lg"
            style="font-size: 1.25rem"
          />
        </Tag>
      </div>

      <div
        v-if="Helper.isImage({ type: type })"
        class="flex items-center justify-center min-h-[150px] transition-[height] duration-300 ease-in-out relative"
        :style="{ height: containerHeight }"
        data-image-container
      >
        <!-- Cropper view with fade transition -->
        <Transition
          enter-active-class="transition-opacity duration-300 ease-in-out"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-300 ease-in-out"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <Cropper
            v-if="showCropper"
            class="w-full h-full border rounded absolute inset-0 bg-gray-100 z-10"
            :src="localUrl"
            :stencil-props="stencilProps"
            :auto-zoom="true"
            :background="true"
            :transitions="false"
            :canvas="{
              width: imageWidth,
              height: imageHeight,
              imageSmoothingEnabled: false,
              imageSmoothingQuality: 'low'
            }"
            ref="cropperRef"
          />
        </Transition>

        <!-- Image view with fade transition -->
        <Transition
          enter-active-class="transition-opacity duration-300 ease-in-out"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-300 ease-in-out"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <img
            v-if="!showCropper"
            :src="localUrl"
            :alt="name"
            class="w-full h-auto max-h-full object-contain border rounded absolute inset-0"
            style="width: 100%; max-width: 100%; object-position: center"
            ref="originalImageRef"
            @load="onImageLoad"
          />
        </Transition>
      </div>
      <VuePdfEmbed
        v-else-if="Helper.isPdf({ type: type })"
        annotation-layer
        text-layer
        :source="url"
      />
      <div
        v-else-if="Helper.isHTML({ type: type })"
        v-html="url"
      ></div>
      <div v-else>{{ `No Preview Available` }}</div>
    </div>
    <template #footer>
      <div class="flex justify-end">
        <Button
          v-if="Helper.isImage({ type: type })"
          label="Save"
          icon="pi pi-check"
          @click="saveAndClose"
          severity="success"
          :disabled="showCropper || isLoading"
          v-tooltip.top="
            showCropper
              ? 'Please save your crop first'
              : isLoading
                ? 'Processing...'
                : ''
          "
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
  import { defineComponent, ref, watch, computed, onMounted } from "vue";
  import Helper from "../utils/helper";
  import { Cropper } from "vue-advanced-cropper";
  import "vue-advanced-cropper/dist/style.css";
  import FullScreenOverlayLoader from "@/components/FullScreenOverlayLoader.vue";

  export default defineComponent({
    name: "ImageEditDialog",
    components: {
      Cropper,
      FullScreenOverlayLoader
    },
    props: {
      visible: {
        type: Boolean,
        required: true
      },
      url: {
        type: String,
        required: true
      },
      name: {
        type: String,
        default: "Preview"
      },
      type: {
        type: String,
        default: "test"
      },
      originalFile: {
        type: Object,
        default: null
      }
    },
    emits: ["update:visible", "save"],
    setup(props, { emit }) {
      // Core reactive state
      const localVisible = ref(props.visible);
      const localUrl = ref(props.url);
      const cropperRef = ref(null);
      const originalImageRef = ref(null);
      const showCropper = ref(false);
      const imageAspectRatio = ref(1);
      const originalBlob = ref(null);
      const isLoading = ref(false);
      const imageWidth = ref(0);
      const imageHeight = ref(0);
      const containerWidth = ref(0);
      const containerHeight = ref("300px");
      const imageEditDialogRef = ref();

      // ===== COMPUTED PROPERTIES =====

      // Stencil props for cropper
      const stencilProps = computed(() => ({
        aspectRatio: null // Allow free aspect ratio with no restrictions
      }));

      // ===== UTILITY FUNCTIONS =====

      const showImageEditDialog = () => {
        imageEditDialogRef.value.maximized = true;
      };

      // Helper to safely revoke and clear URL
      const clearBlobUrl = (url) => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
        return "";
      };

      // Update container width and recalculate height
      const updateContainerDimensions = () => {
        const imageContainer = document.querySelector("[data-image-container]");
        if (!imageContainer) return;

        containerWidth.value = imageContainer.clientWidth;

        // Only recalculate height if we have valid dimensions
        if (imageWidth.value && imageHeight.value) {
          const aspectRatio = imageWidth.value / imageHeight.value;
          const calculatedHeight = containerWidth.value / aspectRatio;

          // Set reasonable bounds
          const maxHeight = 600;
          const minHeight = 150;
          containerHeight.value = `${Math.round(Math.max(Math.min(calculatedHeight, maxHeight), minHeight))}px`;
        }
      };

      // Set image dimensions and update aspect ratio
      const setImageDimensions = (width, height) => {
        imageWidth.value = width;
        imageHeight.value = height;
        imageAspectRatio.value = width / height;

        // Update container height when dimensions change
        updateContainerDimensions();
      };

      // Apply styles to ensure image is properly sized in container
      const applyImageStyles = (imgElement) => {
        if (!imgElement) return;
        imgElement.style.setProperty("width", "100%");
        imgElement.style.setProperty("height", "auto");
        imgElement.style.setProperty("object-fit", "contain");
      };

      // Load image dimensions from URL
      const loadImageDimensions = (url, callback) => {
        if (!url) return;

        const img = new Image();
        img.onload = () => {
          setImageDimensions(img.width, img.height);
          if (callback) callback();
        };
        img.onerror = () => {
          console.error("Error loading image dimensions");
          isLoading.value = false;
        };
        img.src = url;
      };

      // ===== EVENT HANDLERS =====

      // Handler for image load event
      const onImageLoad = () => {
        if (originalImageRef.value) {
          // Set dimensions from the loaded image
          setImageDimensions(
            originalImageRef.value.naturalWidth,
            originalImageRef.value.naturalHeight
          );

          // Apply proper styling to the image
          applyImageStyles(originalImageRef.value);

          // Update container dimensions and finish loading
          setTimeout(() => {
            updateContainerDimensions();
            isLoading.value = false;
          }, 0);
        }
      };

      // ===== CORE IMAGE OPERATIONS =====

      // Store the original blob for editing
      const storeOriginalBlob = async () => {
        try {
          if (!originalBlob.value) {
            if (props.originalFile) {
              originalBlob.value = props.originalFile;
            }
          }
        } catch (error) {
          console.error("Error storing original blob:", error);
        }
      };

      // Toggle cropper visibility
      const toggleCropper = () => {
        isLoading.value = true;

        setTimeout(() => {
          // If activating cropper, ensure dimensions are current
          if (!showCropper.value) {
            loadImageDimensions(localUrl.value);
          }

          showCropper.value = !showCropper.value;
          setTimeout(() => (isLoading.value = false), 100);
        }, 50);
      };

      // Apply cropping to the image
      const applyCrop = async () => {
        if (!cropperRef.value) return;

        try {
          isLoading.value = true;

          // Get crop result
          const { canvas } = cropperRef.value.getResult();
          if (!canvas) return;

          // Convert canvas to blob with full quality
          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, props.type, 1.0)
          );

          // Update image URL and blob
          localUrl.value = clearBlobUrl(localUrl.value);
          localUrl.value = URL.createObjectURL(blob);
          originalBlob.value = blob;

          // Update dimensions based on cropped canvas
          setImageDimensions(canvas.width, canvas.height);

          // Update container and hide cropper
          setTimeout(() => {
            updateContainerDimensions();
            showCropper.value = false;
            setTimeout(() => (isLoading.value = false), 100);
          }, 50);
        } catch (error) {
          console.error("Error applying crop:", error);
          isLoading.value = false;
        }
      };

      // Get crop result as blob
      const getCropResultBlob = async () => {
        if (!cropperRef.value) return null;

        const { canvas } = cropperRef.value.getResult();
        if (canvas) {
          return await new Promise((resolve) =>
            canvas.toBlob(resolve, props.type, 1.0)
          );
        }
        return null;
      };

      // Rotate image by 180 degrees
      const rotateImage = async () => {
        if (!Helper.isImage({ type: props.type })) return;

        try {
          isLoading.value = true;

          // Store original height for smooth transition
          const oldContainerHeight = containerHeight.value;
          const cropperWasActive = showCropper.value;

          // Get and rotate the blob
          const blobToRotate =
            originalBlob.value || (await (await fetch(localUrl.value)).blob());
          const rotatedBlob = await Helper.rotateImage90Degrees(blobToRotate);

          // Update blob and URL
          originalBlob.value = rotatedBlob;
          localUrl.value = clearBlobUrl(localUrl.value);
          localUrl.value = URL.createObjectURL(rotatedBlob);

          // Stabilize container height during transition
          const imageContainer = document.querySelector(
            "[data-image-container]"
          );
          if (imageContainer) {
            imageContainer.style.setProperty("height", oldContainerHeight);
          }

          // Wait for rotated image to load
          loadImageDimensions(localUrl.value, () => {
            // Update container dimensions
            updateContainerDimensions();
            const newHeight = containerHeight.value;

            // Handle based on whether cropper is active
            if (cropperWasActive) {
              // For cropper view, just remove height constraint
              if (imageContainer) {
                imageContainer.style.removeProperty("height");
              }
              isLoading.value = false;
            } else {
              // For regular view, apply proper height and styling
              setTimeout(() => {
                if (imageContainer) {
                  imageContainer.style.setProperty("height", newHeight);
                }

                setTimeout(() => {
                  applyImageStyles(originalImageRef.value);
                  isLoading.value = false;
                }, 50);
              }, 50);
            }
          });
        } catch (error) {
          console.error("Error rotating image:", error);
          isLoading.value = false;
        }
      };

      // Save the current image and close the dialog
      const saveAndClose = async () => {
        try {
          isLoading.value = true;

          // Get the final image blob
          let currentBlob = originalBlob.value;

          // If cropper is active, apply the crop
          if (showCropper.value && cropperRef.value) {
            const croppedBlob = await getCropResultBlob();
            if (croppedBlob) {
              currentBlob = croppedBlob;
            }
          }

          // Fallback to URL if no blob is available
          if (!currentBlob) {
            currentBlob = await (await fetch(localUrl.value)).blob();
          }

          // Apply final compression
          const compressedBlob = await Helper.compressAndRotateFile(
            currentBlob,
            props.type,
            10000 // Standard compression threshold
          );

          // Create file and emit save event
          const imageFile = new File([compressedBlob], props.name, {
            type: props.type
          });
          emit("save", imageFile);
          closeDialog();
        } catch (error) {
          console.error("Error saving image:", error);
        } finally {
          isLoading.value = false;
        }
      };

      // Close the dialog and clean up resources
      const closeDialog = () => {
        localUrl.value = clearBlobUrl(localUrl.value);

        // Reset all state variables
        localVisible.value = false;
        showCropper.value = false;
        originalBlob.value = null;
        isLoading.value = true;
        setImageDimensions(0, 0);
      };

      // ===== WATCHERS =====

      // Watch for dialog visibility changes
      watch(
        () => props.visible,
        (newVal) => {
          localVisible.value = newVal;
          if (newVal) {
            isLoading.value = true;
            showCropper.value = false;

            // Initialize image when dialog opens
            setTimeout(() => {
              loadImageDimensions(localUrl.value);
              storeOriginalBlob().finally(() => {
                isLoading.value = false;
              });
            }, 300);
          }
        }
      );

      // Sync localVisible changes back to parent
      watch(
        () => localVisible.value,
        (newVal) => {
          emit("update:visible", newVal);
        }
      );

      // Watch for URL changes
      watch(
        () => props.url,
        (newUrl) => {
          localUrl.value = newUrl;
          showCropper.value = false;
          originalBlob.value = null;
          isLoading.value = true;

          setTimeout(() => {
            loadImageDimensions(newUrl);
            storeOriginalBlob().finally(() => {
              isLoading.value = false;
            });
          }, 300);
        }
      );

      // ===== LIFECYCLE HOOKS =====

      // Initialize on component mount
      onMounted(() => {
        if (props.visible) {
          setTimeout(() => {
            loadImageDimensions(localUrl.value);
            storeOriginalBlob().finally(() => {
              isLoading.value = false;
            });
          }, 300);
        }

        // Listen for window resize
        const handleResize = () => updateContainerDimensions();
        window.addEventListener("resize", handleResize);
        return () => {
          window.removeEventListener("resize", handleResize);
        };
      });

      return {
        // Reactive state
        localVisible,
        localUrl,
        cropperRef,
        originalImageRef,
        showCropper,
        isLoading,
        imageWidth,
        imageHeight,
        imageEditDialogRef,

        // Computed properties
        containerHeight,
        stencilProps,

        // Methods
        onImageLoad,
        toggleCropper,
        applyCrop,
        rotateImage,
        saveAndClose,
        showImageEditDialog,
        Helper
      };
    }
  });
</script>

<style scoped>
  /* Add some specific styling for the dialog header */
  :deep(.image-edit-dialog .p-dialog-header) {
    overflow: hidden;
  }
</style>
